import React from "react";
import { useLocation } from "react-router";
import { Box, Collapse, Typography, useTheme } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CircleIcon from "@mui/icons-material/Circle";

/** Matches Navigation setup parent (integrations, developer, etc.). */
export function isSetupPath(pathname: string): boolean {
  return (
    pathname.includes("/setup") ||
    pathname.includes("/developer") ||
    pathname.includes("/shopify") ||
    pathname.includes("/bigcommerce") ||
    pathname.includes("/paypal") ||
    pathname.includes("/facebook_ads") ||
    pathname.includes("/woocommerce") ||
    pathname.includes("/thrivecart") ||
    pathname.includes("/clickfunnels") ||
    pathname.includes("/kajabi") ||
    pathname.includes("/stripe") ||
    pathname.includes("/google_ads") ||
    pathname.includes("/bing") ||
    pathname.includes("/tiktok_ads") ||
    pathname.includes("/webhooks") ||
    pathname.includes("/linkedin")
  );
}

/** Which expandable group (Reports / Setup / Settings) should be open for this route. */
export function deriveExpandedGroupFromPath(pathname: string): string | null {
  if (pathname.includes("/settings")) {
    return "Settings";
  }
  if (pathname.includes("/reports")) {
    return "Reports";
  }
  if (isSetupPath(pathname)) {
    return "Setup";
  }
  return null;
}

export type SidebarListItem = {
  title: string;
  icon: React.ReactNode;
  link?: string;
  isSelected?: boolean;
  subItems?: {
    link?: string;
    title: string;
    isSelected?: boolean;
  }[];
};

export type SidebarListProps = {
  listItems: SidebarListItem[];
  headerTitle: string;
  onClickRoute: (selectedRoute: string, selectedSubroute?: string) => void;
};

const NavItem = ({
  listItem,
  onClickRoute,
  expanded,
  onToggleExpand,
}: {
  listItem: SidebarListItem;
  onClickRoute: SidebarListProps["onClickRoute"];
  /** When item has subItems, whether the group is expanded (controlled). */
  expanded: boolean;
  /** Toggle this group; parent collapses other groups (accordion). */
  onToggleExpand: () => void;
}) => {
  const theme = useTheme();
  const { subItems, link, isSelected, icon, title } = listItem;
  const open = Boolean(subItems && expanded);

  const selected = isSelected && !subItems;

  return (
    <Box>
      <Box
        onClick={() => {
          if (subItems) {
            onToggleExpand();
          } else {
            onClickRoute(link!);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          px: "12px",
          py: "10px",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: selected ? theme.palette.primary.main : "transparent",
          color: selected ? "#fff" : theme.palette.primary.main,
          "&:hover": {
            backgroundColor: selected ? theme.palette.primary.main : theme.palette.action.hover,
          },
          userSelect: "none",
        }}>
        {/* Icon */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            flexShrink: 0,
            "& svg": {
              width: 20,
              height: 20,
              color: selected ? "#fff" : theme.palette.primary.main,
            },
          }}>
          {icon}
        </Box>

        {/* Label */}
        <Typography
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            lineHeight: "normal",
            flex: 1,
            color: "inherit",
          }}>
          {title}
        </Typography>

        {/* Expand chevron */}
        {subItems && (
          <Box sx={{ display: "flex", alignItems: "center", "& svg": { width: 18, height: 18 } }}>
            {open ? <ExpandLess /> : <ExpandMore />}
          </Box>
        )}
      </Box>

      {/* Sub-items */}
      {subItems && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box sx={{ mt: "2px" }}>
            {subItems.map(sub => (
              <Box
                key={sub.title}
                onClick={() => onClickRoute(sub.link!)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  pl: "32px",
                  pr: "12px",
                  py: "9px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: sub.isSelected ? theme.palette.primary.main : "transparent",
                  color: sub.isSelected ? "#fff" : theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: sub.isSelected
                      ? theme.palette.primary.main
                      : theme.palette.action.hover,
                  },
                  userSelect: "none",
                }}>
                <CircleIcon
                  sx={{
                    width: "8px !important",
                    height: "8px !important",
                    color: sub.isSelected ? theme.palette.secondary.main : theme.palette.primary.main,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    lineHeight: "normal",
                    color: "inherit",
                  }}>
                  {sub.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const SidebarList = ({ listItems, headerTitle, onClickRoute }: SidebarListProps) => {
  const theme = useTheme();
  const { pathname } = useLocation();

  const [expandedGroup, setExpandedGroup] = React.useState<string | null>(() =>
    deriveExpandedGroupFromPath(pathname)
  );

  React.useEffect(() => {
    setExpandedGroup(deriveExpandedGroupFromPath(pathname));
  }, [pathname]);

  const handleToggleExpand = (title: string) => {
    setExpandedGroup(prev => (prev === title ? null : title));
  };

  return (
    <Box
      component="nav"
      sx={{
        width: "278px",
        padding: 3,
        paddingBottom: 0,
      }}>
      {/* Section header */}
      {headerTitle && (
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 600,
            color: theme.palette.primary.main,
            opacity: 0.8,
            mb: 1,
            pl: "4px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}>
          {headerTitle}
        </Typography>
      )}

      {/* Nav items */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {listItems.map(listItem => (
          <NavItem
            key={listItem.title}
            listItem={listItem}
            onClickRoute={onClickRoute}
            expanded={listItem.subItems ? expandedGroup === listItem.title : false}
            onToggleExpand={() => handleToggleExpand(listItem.title)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default SidebarList;
